const CACHE_BUFFER_MS = 30_000

interface AuthSessionData {
  expired_at: number | null
  issued_at: number | null
  token: string | null
  ttl_seconds: number | null
  user_id: number | string | null
  user_roles: string[] | null
  user_type: string | null
}

interface AuthSessionResponse {
  data: AuthSessionData | null
}

interface CachedStudentToken {
  expiresAtMs: number | null
  studentId: string
  token: string
}

export function authSessionUrlForLoginUrl(loginUrl: string): string {
  try {
    return new URL('/auth/session', loginUrl).href
  } catch {
    return '/auth/session'
  }
}

export class SessionToken {
  private cachedStudentToken: CachedStudentToken | undefined = undefined

  constructor(private readonly authSessionUrl: string) {}

  async getTokenForStudent(studentId: number | string): Promise<string | null> {
    const expectedStudentId = String(studentId)

    if (this.hasUsableCachedToken(expectedStudentId)) {
      return this.cachedStudentToken?.token ?? null
    }

    const data = await this.requestSessionData()

    if (!this.isValidStudentSession(data, expectedStudentId)) {
      this.cachedStudentToken = undefined
      return null
    }

    const expiresAtMs =
      typeof data.expired_at === 'number' ? data.expired_at * 1000 : null

    if (this.shouldCacheToken(expiresAtMs)) {
      this.cachedStudentToken = {
        expiresAtMs,
        studentId: expectedStudentId,
        token: data.token,
      }
    } else {
      this.cachedStudentToken = undefined
    }

    return data.token
  }

  private async requestSessionData(): Promise<AuthSessionData | null> {
    const response = await globalThis.fetch(this.authSessionUrl, {
      credentials: 'include',
      headers: {
        accept: 'application/json',
      },
    })

    if (!response.ok) {
      throw new Error(
        `Session token request failed with status ${String(response.status)}`,
      )
    }

    const { data } = (await response.json()) as AuthSessionResponse
    return data
  }

  private hasUsableCachedToken(studentId: string): boolean {
    const cachedStudentToken = this.cachedStudentToken

    if (!cachedStudentToken) return false
    if (cachedStudentToken.studentId !== studentId) return false

    return this.shouldCacheToken(cachedStudentToken.expiresAtMs)
  }

  private isValidStudentSession(
    data: AuthSessionData | null,
    expectedStudentId: string,
  ): data is AuthSessionData & { token: string } {
    return Boolean(
      data &&
        typeof data.token === 'string' &&
        data.token.length > 0 &&
        data.user_type === 'student' &&
        String(data.user_id) === expectedStudentId,
    )
  }

  private shouldCacheToken(expiresAtMs: number | null): boolean {
    return expiresAtMs === null || expiresAtMs > Date.now() + CACHE_BUFFER_MS
  }
}
